import { createElement, ReactNode } from 'react';

const htmlTagRegularExpression = /<(\w+)([^>]*)>(?:([\s\S]*?)<\/\1>)?/;
const htmlAttributeRegularExpression = /(\w+(?:-\w+)*)\s*=\s*(["'])(.*?)\2/;
const disallowedHtmlTags = /script|link|style/i;

function convertHtmlAttributeName(name: string) {
    switch (name.toLowerCase()) {
        case 'class':
            return 'className';
        case 'for':
            return 'htmlFor';
        default:
            return name;
    }
}

let textarea: HTMLTextAreaElement | undefined;
export function decodeEntities(input: string) {
    textarea ??= window.document.createElement('textarea');
    textarea.innerHTML = input;
    return textarea.value;
}

function getHtmlAttributes(attributes: string) {
    const result: Record<string, string> = {};
    let attrs = attributes;
    let match = htmlAttributeRegularExpression.exec(attrs);
    while (match) {
        result[convertHtmlAttributeName(match[1])] = decodeEntities(match[3]);
        attrs = attrs.substring(match.index + match[0].length);
        match = htmlAttributeRegularExpression.exec(attrs);
    }
    return result;
}

export interface Node {
    tagName: string;
    attributes: Record<string, unknown>;
    children?: ReactNode[];
}

export function parseHtml(html: string | undefined, onCreateElement?: (node: Node) => Node | undefined): ReactNode[] | undefined {
    if (!html) return undefined;
    const nodes: ReactNode[] = [];
    let text = html.match(/<body[^>]*>([\r\n\s\S]*?)<\/body/)?.[1] ?? html;

    let match = htmlTagRegularExpression.exec(text);
    while (match) {
        const textBeforeTag = text.substring(0, match.index);
        if (textBeforeTag !== '') {
            nodes.push(textBeforeTag);
        }

        if (!disallowedHtmlTags.test(match[1])) {
            const attributes = getHtmlAttributes(match[2]);
            let node: Node | undefined = {
                tagName: match[1],
                attributes,
                children: parseHtml(match[3], onCreateElement),
            };
            if (onCreateElement) {
                node = onCreateElement(node);
            }
            if (node && node.tagName) {
                node.attributes.key = nodes.length;
                nodes.push(createElement(node.tagName, node.attributes, node.children));
            }
        }

        text = text.substring(match.index + match[0].length);
        match = htmlTagRegularExpression.exec(text);
    }

    if (text) {
        nodes.push(text);
    }

    return nodes.filter(Boolean).map((x) => (typeof x === 'string' ? decodeEntities(x) : x));
}
